// ARGOS · EVIDENCE — specialist. Reads today's argos_sessions (the only time the
// tool records: duration_seconds per category/domain) and argos_blocks (blocked /
// overridden). ARGOS stores no per-task plan, so "variance" is tracked time vs
// today's planned minutes from the orb's own items.
import { supabase } from '../../../../lib/supabase';
import { dateKeyOf } from '../../time';
import type { OrbWidgetModel, WidgetCtx } from '../types';
import { emptyModel, openAction, useLoad } from './util';

const BASE = { id: 'argos', toolId: 'argos', orderFunction: 'revise', title: 'EVIDENCE', deityName: 'ARGOS', agentId: 'argos' } as const;

interface Evidence {
  seconds: number;
  top: { name: string; seconds: number } | null;
  blocks: number;
  overridden: number;
}

const hm = (min: number) => {
  const a = Math.abs(Math.round(min));
  return a >= 60 ? `${Math.floor(a / 60)}h ${a % 60}m` : `${a}m`;
};
const signed = (min: number) => `${min < 0 ? '-' : '+'}${hm(min)}`;

export function useArgosWidget(c: WidgetCtx): OrbWidgetModel {
  const uid = c.userId;
  const dayKey = dateKeyOf(c.now);
  const { value: ev } = useLoad<Evidence>(
    uid
      ? async () => {
          const from = new Date(c.now.getFullYear(), c.now.getMonth(), c.now.getDate());
          const to = new Date(from.getTime() + 86400000);
          const [s, b] = await Promise.all([
            supabase.from('argos_sessions').select('category_slug, domain, duration_seconds').eq('user_id', uid).gte('started_at', from.toISOString()).lt('started_at', to.toISOString()),
            supabase.from('argos_blocks').select('overridden').gte('blocked_at', from.toISOString()).lt('blocked_at', to.toISOString()),
          ]);
          const sess = (s.data ?? []) as Array<{ category_slug: string | null; domain: string | null; duration_seconds: number | null }>;
          const blocks = (b.data ?? []) as Array<{ overridden: boolean | null }>;
          const by: Record<string, number> = {};
          let seconds = 0;
          for (const r of sess) {
            const sec = r.duration_seconds ?? 0;
            seconds += sec;
            const k = r.category_slug || r.domain || 'other';
            by[k] = (by[k] ?? 0) + sec;
          }
          const best = Object.entries(by).sort((x, y) => y[1] - x[1])[0];
          return { seconds, top: best ? { name: best[0], seconds: best[1] } : null, blocks: blocks.length, overridden: blocks.filter((x) => x.overridden).length };
        }
      : null,
    [uid, dayKey, c.tick],
  );

  if (!ev || (ev.seconds === 0 && ev.blocks === 0)) return emptyModel(BASE, c, 'No evidence logged today');

  const actualMin = ev.seconds / 60;
  const plannedMin = c.data.items.filter((i) => i.date === dayKey).reduce((n, i) => n + (i.minutes || 0), 0);
  const hasPlan = plannedMin > 0;
  const variance = actualMin - plannedMin;
  const bigVar = hasPlan && Math.abs(variance) >= 30;

  const lines = [hasPlan ? `Tracked ${hm(actualMin)} · planned ${hm(plannedMin)}` : `Tracked ${hm(actualMin)} · nothing planned`];
  if (ev.top) lines.push(`Largest: ${ev.top.name} · ${hm(ev.top.seconds / 60)}`);
  if (ev.blocks > 0) lines.push(`${ev.blocks} block${ev.blocks === 1 ? '' : 's'} · ${ev.overridden} overridden`);

  const status: OrbWidgetModel['status'] = ev.overridden > 0 || bigVar ? 'attention' : 'active';
  return {
    ...BASE,
    status,
    summary: hasPlan ? `Plan variance: ${signed(variance)}` : `Tracked ${hm(actualMin)} today`,
    metrics: [
      { label: 'Tracked', value: hm(actualMin) },
      ...(hasPlan ? [{ label: 'Planned', value: hm(plannedMin) }] : []),
      ...(ev.blocks > 0 ? [{ label: 'Overridden', value: ev.overridden, tone: (ev.overridden > 0 ? 'warning' : 'neutral') as 'warning' | 'neutral' }] : []),
    ],
    alert: status === 'attention' ? (ev.overridden > 0 ? `${ev.overridden} block${ev.overridden === 1 ? '' : 's'} overridden` : `Variance ${signed(variance)}`) : null,
    lines,
    primaryAction: { label: 'Inspect variance', run: () => c.openTool('argos'), tone: 'gold' },
    secondaryActions: [openAction(c, 'argos', 'Argos')],
    signals: { active: true, alerted: status === 'attention' },
  };
}
